import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { Container, Box, Typography } from '@mui/material';
import FinmapleComparator from '../components/Comparator/FinmapleComparator';
import Shortlist from '../components/Comparator/Shortlist';
import CrossLinks from '../components/Layout/CrossLinks';
import NewsletterSection from '../components/Layout/NewsletterSection';
import TrustStrip from '../components/Layout/TrustStrip';
import SEOHead from '../components/SEO/SEOHead';
import PageHeader from '../components/Layout/PageHeader';
import AtomicAnswer from '../components/SEO/AtomicAnswer';
import FAQSection from '../components/SEO/FAQSection';
import SectionDivider from '../components/Home/SectionDivider';
import EditorialReview from '../components/Layout/EditorialReview';
import { colors } from '../theme/colorTokens';

/* ── i18n content ───────────────────────────────────────────────────── */

const content = {
  es: {
    schemaName: 'Comparador de Cuentas Bancarias - FinMaple',
    schemaDesc: 'Compara las mejores cuentas bancarias en Canadá para recién llegados a Canadá. Chequing, savings, digitales - con programas newcomer.',
    seoTitle: 'Mejores Cuentas Bancarias para Newcomers en Canadá 2026',
    seoDesc: 'Compara 12 cuentas bancarias para recién llegados a Canadá. Programas newcomer, bonos de bienvenida hasta $800, cuentas sin cuota.',
    headerTitle: 'Las mejores cuentas bancarias para newcomers en Canadá - comparadas y verificadas para 2026',
    headerSubtitle: 'Analizamos cuotas, interés, bonos y programas newcomer de 12 instituciones canadienses.',
    breadcrumb: 'Cuentas Bancarias',
    atomic: 'La mejor cuenta para un newcomer depende de si prefieres $0 fee (EQ Bank, Simplii, Tangerine), el bono más alto ($800 en BMO), o el fee waiver más largo (3 años en National Bank). Todas las cuentas en este comparador aceptan newcomers sin historial crediticio canadiense.',
    shortlistTitle: 'Respuesta rápida',
    shortlistBadge1: 'Mejor $0 fees',
    shortlistDetail1: '$0/mes, hasta 2.75% interés en chequing, sin mínimos. 100% digital.',
    shortlistCta1: 'Abrir cuenta',
    shortlistBadge2: 'Mejor para newcomer',
    shortlistDetail2: '3 años sin fee mensual, tarjeta de crédito sin historial, sucursales en Quebec.',
    shortlistCta2: 'Abrir cuenta',
    shortlistBadge3: 'Mejor banco grande',
    shortlistDetail3: 'Bono hasta $800, fee waiver 12 meses, acceso a tarjeta newcomer, sucursales nationwide.',
    shortlistCta3: 'Abrir cuenta',
    howWeChooseTitle: 'Cómo elegimos',
    howWeChooseBody: 'No elegimos solo por promos. Priorizamos costo real, facilidad de uso, beneficios para newcomers y claridad de condiciones. FinMaple puede recibir compensación si abres una cuenta desde algunos enlaces. Eso no cambia nuestras calificaciones.',
    crossLinksTitle: 'Siguiente paso',
    crossLink1: 'Tarjetas para newcomers',
    crossLink2: 'Calculadora salario neto',
    crossLink3: 'Presupuesta tu vida',
    crossLink4: 'Guía de impuestos',
    crossLink5: 'Empieza aquí',
    faqs: [
      {
        question: '¿Puedo abrir una cuenta bancaria en Canadá sin historial crediticio?',
        answer: 'Sí. Todos los bancos canadienses permiten abrir cuentas sin historial crediticio. Los Big 5 (RBC, TD, BMO, Scotiabank, CIBC) tienen programas específicos para newcomers que incluyen exención de cuotas por 12-36 meses y acceso a tarjetas de crédito sin historial.',
      },
      {
        question: '¿Cuál es la mejor cuenta para recién llegados a Canadá?',
        answer: 'Depende de tus prioridades: si buscas $0 fee y alto interés, EQ Bank (2.75%) o Wealthsimple son excelentes. Si prefieres servicio en persona, BMO NewStart ($800 bono) o National Bank (3 años sin fee) destacan. En Quebec, Tangerine y Desjardins son opciones fuertes.',
      },
      {
        question: '¿Puedo abrir una cuenta bancaria antes de llegar a Canadá?',
        answer: 'Sí. Scotiabank (StartRight) y CIBC (Smart Arrival) permiten abrir cuenta desde tu país de origen. Esto te permite tener una cuenta activa el día que llegas, facilitando transferencias y primeros gastos.',
      },
      {
        question: '¿Qué es CDIC y por qué importa?',
        answer: 'CDIC (Canada Deposit Insurance Corporation) es el seguro federal que protege tus depósitos hasta $100,000 por categoría si el banco quiebra. La mayoría de los bancos están cubiertos. Wealthsimple ofrece cobertura hasta $1M usando múltiples categorías.',
      },
      {
        question: '¿Puedo tener cuentas en múltiples bancos?',
        answer: 'Sí, y es recomendable. Una estrategia común: una cuenta chequing sin fee (Simplii/Tangerine) para gastos diarios + una cuenta savings de alto interés (EQ Bank/Wealthsimple) para ahorros + una cuenta Big 5 para el bono de bienvenida.',
      },
    ],
  },
  en: {
    schemaName: 'Bank Account Comparator - FinMaple',
    schemaDesc: 'Compare the best bank accounts in Canada for newcomers. Chequing, savings, digital banks — with newcomer programs.',
    seoTitle: 'Best Bank Accounts for Newcomers in Canada 2026',
    seoDesc: 'Compare the best bank accounts for newcomers in Canada. No-fee accounts, newcomer programs, and welcome bonuses compared.',
    headerTitle: 'The best bank accounts for newcomers in Canada — compared and verified for 2026',
    headerSubtitle: 'We analyze fees, interest rates, bonuses and newcomer programs from 12 Canadian institutions.',
    breadcrumb: 'Bank Accounts',
    atomic: 'The best account for a newcomer depends on whether you prefer $0 fees (EQ Bank, Simplii, Tangerine), the highest bonus ($800 at BMO), or the longest fee waiver (3 years at National Bank). All accounts in this comparator accept newcomers without Canadian credit history.',
    shortlistTitle: 'Quick answer',
    shortlistBadge1: 'Best $0 fees',
    shortlistDetail1: '$0/month, up to 2.75% interest on your chequing balance, no minimums. 100% digital.',
    shortlistCta1: 'Open account',
    shortlistBadge2: 'Best for newcomers',
    shortlistDetail2: '3 years with no monthly fee, credit card with no credit history required, branches in Quebec.',
    shortlistCta2: 'Open account',
    shortlistBadge3: 'Best big bank',
    shortlistDetail3: 'Bonus up to $800, 12-month fee waiver, newcomer card access, nationwide branches.',
    shortlistCta3: 'Open account',
    howWeChooseTitle: 'How we choose',
    howWeChooseBody: 'We don\'t select based on promotions alone. We prioritize real cost, ease of use, newcomer benefits and clear conditions. FinMaple may receive compensation if you open an account through some links. That doesn\'t change our ratings.',
    crossLinksTitle: 'Next step',
    crossLink1: 'Cards for newcomers',
    crossLink2: 'Net salary calculator',
    crossLink3: 'Budget your life',
    crossLink4: 'Tax guide',
    crossLink5: 'Get started',
    faqs: [
      {
        question: '¿Puedo abrir una cuenta bancaria en Canadá sin historial crediticio?',
        answer: 'Sí. Todos los bancos canadienses permiten abrir cuentas sin historial crediticio. Los Big 5 (RBC, TD, BMO, Scotiabank, CIBC) tienen programas específicos para newcomers que incluyen exención de cuotas por 12-36 meses y acceso a tarjetas de crédito sin historial.',
      },
      {
        question: '¿Cuál es la mejor cuenta para recién llegados a Canadá?',
        answer: 'Depende de tus prioridades: si buscas $0 fee y alto interés, EQ Bank (2.75%) o Wealthsimple son excelentes. Si prefieres servicio en persona, BMO NewStart ($800 bono) o National Bank (3 años sin fee) destacan. En Quebec, Tangerine y Desjardins son opciones fuertes.',
      },
      {
        question: '¿Puedo abrir una cuenta bancaria antes de llegar a Canadá?',
        answer: 'Sí. Scotiabank (StartRight) y CIBC (Smart Arrival) permiten abrir cuenta desde tu país de origen. Esto te permite tener una cuenta activa el día que llegas, facilitando transferencias y primeros gastos.',
      },
      {
        question: '¿Qué es CDIC y por qué importa?',
        answer: 'CDIC (Canada Deposit Insurance Corporation) es el seguro federal que protege tus depósitos hasta $100,000 por categoría si el banco quiebra. La mayoría de los bancos están cubiertos. Wealthsimple ofrece cobertura hasta $1M usando múltiples categorías.',
      },
      {
        question: '¿Puedo tener cuentas en múltiples bancos?',
        answer: 'Sí, y es recomendable. Una estrategia común: una cuenta chequing sin fee (Simplii/Tangerine) para gastos diarios + una cuenta savings de alto interés (EQ Bank/Wealthsimple) para ahorros + una cuenta Big 5 para el bono de bienvenida.',
      },
    ],
  },
  fr: {
    schemaName: 'Comparateur de comptes bancaires - FinMaple',
    schemaDesc: 'Comparez les meilleurs comptes bancaires au Canada pour les nouveaux arrivants. Comptes courants, épargne, banques numériques — avec programmes pour nouveaux arrivants.',
    seoTitle: 'Meilleurs comptes bancaires pour nouveaux arrivants au Canada 2026',
    seoDesc: 'Comparez les meilleurs comptes bancaires pour les nouveaux arrivants au Canada. Comptes sans frais, programmes pour nouveaux arrivants et bonus de bienvenue comparés.',
    headerTitle: 'Les meilleurs comptes bancaires pour les nouveaux arrivants au Canada — comparés et vérifiés pour 2026',
    headerSubtitle: 'Nous analysons les frais, les taux d\'intérêt, les bonus et les programmes pour nouveaux arrivants de 12 institutions canadiennes.',
    breadcrumb: 'Comptes bancaires',
    atomic: 'Le meilleur compte pour un nouvel arrivant dépend de vos préférences : 0 $ de frais (EQ Bank, Simplii, Tangerine), le bonus le plus élevé (800 $ chez BMO) ou l\'exonération de frais la plus longue (3 ans à la Banque Nationale). Tous les comptes de ce comparateur acceptent les nouveaux arrivants sans historique de crédit canadien.',
    shortlistTitle: 'Réponse rapide',
    shortlistBadge1: 'Meilleur 0 $ frais',
    shortlistDetail1: '0 $/mois, jusqu\'à 2,75 % d\'intérêt sur votre solde de compte courant, sans minimum. 100 % numérique.',
    shortlistCta1: 'Ouvrir un compte',
    shortlistBadge2: 'Meilleur pour les nouveaux arrivants',
    shortlistDetail2: '3 ans sans frais mensuels, carte de crédit sans historique de crédit requis, succursales au Québec.',
    shortlistCta2: 'Ouvrir un compte',
    shortlistBadge3: 'Meilleure grande banque',
    shortlistDetail3: 'Bonus jusqu\'à 800 $, exonération de frais pendant 12 mois, accès à une carte pour nouveaux arrivants, succursales partout au Canada.',
    shortlistCta3: 'Ouvrir un compte',
    howWeChooseTitle: 'Comment nous choisissons',
    howWeChooseBody: 'Nous ne sélectionnons pas selon les promotions seules. Nous priorisons le coût réel, la facilité d\'utilisation, les avantages pour les nouveaux arrivants et la clarté des conditions. FinMaple peut recevoir une compensation si vous ouvrez un compte via certains liens. Cela ne modifie pas nos évaluations.',
    crossLinksTitle: 'Prochaine étape',
    crossLink1: 'Cartes pour nouveaux arrivants',
    crossLink2: 'Calculatrice de salaire net',
    crossLink3: 'Planifiez votre budget',
    crossLink4: 'Guide fiscal',
    crossLink5: 'Commencer ici',
    faqs: [
      {
        question: '¿Puedo abrir una cuenta bancaria en Canadá sin historial crediticio?',
        answer: 'Sí. Todos los bancos canadienses permiten abrir cuentas sin historial crediticio. Los Big 5 (RBC, TD, BMO, Scotiabank, CIBC) tienen programas específicos para newcomers que incluyen exención de cuotas por 12-36 meses y acceso a tarjetas de crédito sin historial.',
      },
      {
        question: '¿Cuál es la mejor cuenta para recién llegados a Canadá?',
        answer: 'Depende de tus prioridades: si buscas $0 fee y alto interés, EQ Bank (2.75%) o Wealthsimple son excelentes. Si prefieres servicio en persona, BMO NewStart ($800 bono) o National Bank (3 años sin fee) destacan. En Quebec, Tangerine y Desjardins son opciones fuertes.',
      },
      {
        question: '¿Puedo abrir una cuenta bancaria antes de llegar a Canadá?',
        answer: 'Sí. Scotiabank (StartRight) y CIBC (Smart Arrival) permiten abrir cuenta desde tu país de origen. Esto te permite tener una cuenta activa el día que llegas, facilitando transferencias y primeros gastos.',
      },
      {
        question: '¿Qué es CDIC y por qué importa?',
        answer: 'CDIC (Canada Deposit Insurance Corporation) es el seguro federal que protege tus depósitos hasta $100,000 por categoría si el banco quiebra. La mayoría de los bancos están cubiertos. Wealthsimple ofrece cobertura hasta $1M usando múltiples categorías.',
      },
      {
        question: '¿Puedo tener cuentas en múltiples bancos?',
        answer: 'Sí, y es recomendable. Una estrategia común: una cuenta chequing sin fee (Simplii/Tangerine) para gastos diarios + una cuenta savings de alto interés (EQ Bank/Wealthsimple) para ahorros + una cuenta Big 5 para el bono de bienvenida.',
      },
    ],
  },
};

/* ── Page ────────────────────────────────────────────────────────────── */

const ComparadorCuentasPage = () => {
  const { i18n } = useTranslation();
  const lng = i18n.language || 'es';
  const c = content[lng] || content.es;
  const { hash } = useLocation();

  // Auto-scroll to comparador when landing without a hash
  useEffect(() => {
    if (!hash) {
      const timer = setTimeout(() => {
        const el = document.getElementById('comparador');
        if (el) {
          const y = el.getBoundingClientRect().top + window.scrollY - 80;
          window.scrollTo({ top: y, behavior: 'smooth' });
        }
      }, 150);
      return () => clearTimeout(timer);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'FinancialService',
    name: c.schemaName,
    description: c.schemaDesc,
    provider: { '@type': 'Organization', name: 'FinMaple', url: 'https://www.finmaple.com' },
    dateModified: '2026-03-09',
  };

  return (
  <Box sx={{ bgcolor: '#FFFFFF' }}>
    <SEOHead
      seoKey="accountComparator"
      title={c.seoTitle}
      description={c.seoDesc}
      canonical="/comparar-cuentas"
      routeKey="accountComparator"
      schema={schema}
      noIndex={false}
    />
    <PageHeader
      variant="functional"
      title={c.headerTitle}
      subtitle={c.headerSubtitle}
      breadcrumbs={[{ label: c.breadcrumb }]}
      maxWidth="lg"
    />
    <TrustStrip />
    <SectionDivider from="#F7F9FC" to="#FFFFFF" />
    <EditorialReview />
    <Container maxWidth="lg" sx={{ mb: 2 }}>
      <AtomicAnswer text={c.atomic} />
    </Container>
    <Container maxWidth="lg" sx={{ mb: { xs: 3, md: 4 } }}>
      <Shortlist
        title={c.shortlistTitle}
        items={[
          {
            badge: c.shortlistBadge1,
            name: 'EQ Bank Personal Account',
            detail: c.shortlistDetail1,
            cta: c.shortlistCta1,
            link: '#comparador',
          },
          {
            badge: c.shortlistBadge2,
            name: 'National Bank Newcomer',
            detail: c.shortlistDetail2,
            cta: c.shortlistCta2,
            link: '#comparador',
          },
          {
            badge: c.shortlistBadge3,
            name: 'BMO NewStart Program',
            detail: c.shortlistDetail3,
            cta: c.shortlistCta3,
            link: '#comparador',
          },
        ]}
      />
    </Container>
    <Container maxWidth="lg" id="comparador">
      <FinmapleComparator category="bank_accounts" />
    </Container>
    <Container maxWidth="lg" sx={{ mt: { xs: 3, md: 4 } }}>
      <Box sx={{ bgcolor: colors.softBg, borderRadius: '12px', p: { xs: 2.5, md: 3 }, mb: 3 }}>
        <Typography sx={{ fontWeight: 600, fontSize: '15px', color: colors.textPrimary, mb: 1 }}>
          {c.howWeChooseTitle}
        </Typography>
        <Typography sx={{ fontSize: '14px', color: colors.textSecondary, lineHeight: 1.7 }}>
          {c.howWeChooseBody}
        </Typography>
      </Box>
      <CrossLinks
        title={c.crossLinksTitle}
        links={[
          { label: c.crossLink1, to: '/tarjetas-de-credito' },
          { label: c.crossLink2, to: '/herramientas/calculadora-salario-neto' },
          { label: c.crossLink3, to: '/budget-planner' },
          { label: c.crossLink4, to: '/blog/guia-impuestos-latinos-quebec-2026' },
          { label: c.crossLink5, to: '/empieza-aqui' },
        ]}
      />
    </Container>
    <Box sx={{ bgcolor: '#F7F9FC', mt: { xs: 4, md: 6 } }}>
      <Container maxWidth="lg" sx={{ py: { xs: 6, md: 8 } }}>
        <FAQSection faqs={c.faqs} />
      </Container>
    </Box>
    <NewsletterSection />
  </Box>
  );
};

export default ComparadorCuentasPage;
